/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { conversationsService } from '../../services/conversations.service';
import { agentsService } from '../../services/agents.service';
import type { Conversation } from '../../types/conversation.types';
import type { Agent } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useMyPermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ConversationThread, { buildChatUi } from './conversation-thread';

const ConversationView = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { has } = useMyPermissions();
  const canSend = has(PERMISSIONS.CONVERSATION_EXECUTE);
  const canEdit = has(PERMISSIONS.CONVERSATION_UPDATE);

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const reload = () => conversationsService.getById(id)
    .then((c) => {
      setConversation(c);
      setTitleDraft(c.title || '');
      // Load the agent so its conversation.ui (branding, welcome, starters) applies here too.
      if (c.agentMasterId) agentsService.getById(c.agentMasterId).then(setAgent).catch(() => setAgent(null));
    })
    .catch((e) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Failed to load the conversation'), life: 8000 }));

  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ui = buildChatUi(agent);
  const accentStyle = ui.accentColor ? { backgroundColor: `${ui.accentColor}1a`, color: ui.accentColor } : undefined;

  const saveTitle = async () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!conversation || next === (conversation.title || '')) return;
    try {
      const updated = await conversationsService.update(id, { title: next });
      setConversation(updated);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Could not rename'), life: 6000 });
      setTitleDraft(conversation.title || '');
    }
  };

  return (
    <PermissionGuard jobName={JOB_NAMES.CONVERSATIONS}>
      <Toast ref={toast} position="top-right" />
      {/* Viewport-based height (always resolves, unlike a % chain through the layout) so
          the view fits the pane exactly; overflow-hidden means ONLY the thread scrolls.
          Underscores = the spaces calc() needs; without them the height is dropped. */}
      {/* h-full fills the content pane exactly (pane has a definite height via the layout);
          overflow-hidden means ONLY the thread scrolls — never the pane or the browser. */}
      <div className="flex h-full flex-col overflow-hidden p-4" data-testid="SCR-Conversation-View">
        {/* Header */}
        <div className="mb-3 flex items-center gap-2.5">
          <Button icon="pi pi-arrow-left" rounded text size="small" aria-label="Back to conversations" onClick={() => navigate('/conversations')} />
          {/* Branded co-pilot avatar */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" style={accentStyle}>
            <i className={ui.icon} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            {editingTitle ? (
              <Input autoFocus value={titleDraft} onChange={setTitleDraft} onBlur={saveTitle}
                onKeyDown={(e: any) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setTitleDraft(conversation?.title || ''); setEditingTitle(false); } }} />
            ) : (
              <button type="button" disabled={!canEdit} onClick={() => canEdit && setEditingTitle(true)}
                className="truncate text-left text-base font-semibold text-gray-900 hover:underline disabled:cursor-default disabled:no-underline dark:text-gray-100"
                title={canEdit ? 'Click to rename' : undefined}>
                {conversation?.title || 'Untitled conversation'}
              </button>
            )}
            <div className="truncate text-xs text-ink-muted">{ui.title}</div>
          </div>
          {/* Open the full chat workspace with this conversation active. */}
          <Button icon="pi pi-window-maximize" rounded text size="small" aria-label="Open full chat"
            title="Open full chat workspace"
            onClick={() => navigate(`/chat?c=${id}`)} />
        </div>

        {/* Thread (shared) — flex column so the thread grows to fill and only it scrolls. */}
        <div className="ui-panel flex min-h-0 flex-1 flex-col overflow-hidden">
          <ConversationThread conversationId={id} agentCode={conversation?.agentCode || ''} canSend={canSend} ui={ui} onTurnDone={reload} />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default ConversationView;
